/**
 * PokeCCC shared localisation runtime.
 *
 * Loaded synchronously from <head> (see snippets/pokeccc-locale-config.liquid)
 * because the cross-domain language decision has to be made before first
 * paint. Everything else — the switcher UI, the free shipping bar — is
 * deferred and builds on the `window.PokeCCC` namespace exposed here.
 *
 * Owns:
 *   PokeCCC.config    parsed #pokeccc-config JSON
 *   PokeCCC.prefs     manual language / country choices (localStorage, TTL'd)
 *   PokeCCC.currency  the ONLY place that touches Convercy's storage keys
 *   PokeCCC.routing   locale-aware URL resolution and navigation
 *   PokeCCC.geo       shared, session-cached country detection (one request)
 *   PokeCCC.fastPath  what the pre-paint pass decided
 */
(function () {
  'use strict';

  var LOG_PREFIX = '[PokecccLocale]';
  var CONFIG_ID = 'pokeccc-config';
  var REDIRECT_COUNT_KEY = 'pokeccc_redirects';
  var REDIRECT_TIME_KEY = 'pokeccc_last_redirect';
  var MAX_REDIRECTS = 3;
  /**
   * An automatic redirect that lands somewhere and immediately wants to move
   * again is a bug, not a journey. Two hops in quick succession are refused so
   * the visitor is never bounced twice for one page view.
   */
  var REDIRECT_COOLDOWN_MS = 8000;

  /** Which domain owns which language. Fixed by the two-storefront split. */
  var LANG_OWNER = { cs: 'cz', sk: 'cz', en: 'com' };

  /* ------------------------------------------------------------------ */
  /* Storage helpers — every access is guarded (Safari private mode)     */
  /* ------------------------------------------------------------------ */

  function store(kind) {
    try {
      return window[kind];
    } catch (e) {
      return null;
    }
  }

  var local = store('localStorage');
  var session = store('sessionStorage');

  function getItem(target, key) {
    if (!target) return null;
    try {
      return target.getItem(key);
    } catch (e) {
      return null;
    }
  }

  function setItem(target, key, value) {
    if (!target) return;
    try {
      target.setItem(key, value);
    } catch (e) {
      /* quota exceeded or private mode */
    }
  }

  function removeItem(target, key) {
    if (!target) return;
    try {
      target.removeItem(key);
    } catch (e) {
      /* ignore */
    }
  }

  function readJson(target, key) {
    var raw = getItem(target, key);
    if (!raw) return null;
    try {
      return JSON.parse(raw);
    } catch (e) {
      removeItem(target, key);
      return null;
    }
  }

  /* ------------------------------------------------------------------ */
  /* Config                                                              */
  /* ------------------------------------------------------------------ */

  function readConfig() {
    var el = document.getElementById(CONFIG_ID);
    if (!el) {
      console.error(LOG_PREFIX, 'Missing #' + CONFIG_ID);
      return null;
    }
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      console.error(LOG_PREFIX, 'Invalid config JSON', e);
      return null;
    }
  }

  var config = readConfig();
  if (!config) {
    window.PokeCCC = { config: null, ready: false, abort: true };
    return;
  }

  function isValidLang(lang) {
    return !!lang && config.validLangs.indexOf(lang) !== -1;
  }

  /* ------------------------------------------------------------------ */
  /* Manual preferences                                                  */
  /* ------------------------------------------------------------------ */

  /**
   * Reads a TTL'd preference. Two legacy shapes are supported so that
   * choices made before this refactor keep working: `{ lang, expire }` for
   * the language and `{ expire }` for the country flag.
   */
  function readExpiring(key) {
    var obj = readJson(local, key);
    if (!obj || typeof obj.expire !== 'number') {
      if (obj) removeItem(local, key);
      return null;
    }
    if (Date.now() > obj.expire) {
      removeItem(local, key);
      return null;
    }
    return obj;
  }

  function writeExpiring(key, extra) {
    var payload = { expire: Date.now() + config.manualTtlMs };
    if (extra) {
      for (var prop in extra) {
        if (Object.prototype.hasOwnProperty.call(extra, prop)) {
          payload[prop] = extra[prop];
        }
      }
    }
    setItem(local, key, JSON.stringify(payload));
  }

  var prefs = {
    getLang: function () {
      var obj = readExpiring(config.manualKey);
      return obj && isValidLang(obj.lang) ? obj.lang : null;
    },
    setLang: function (lang) {
      if (!isValidLang(lang)) return;
      writeExpiring(config.manualKey, { lang: lang });
    },
    /** True when the visitor picked a shipping country in the native form. */
    hasCountry: function () {
      return !!readExpiring(config.countryManualKey);
    },
    setCountry: function () {
      writeExpiring(config.countryManualKey);
    },
  };

  /* ------------------------------------------------------------------ */
  /* Convercy adapter                                                    */
  /*                                                                     */
  /* The theme drives the currency app by writing its own storage keys.   */
  /* That is a private contract with a third party, so it is confined to  */
  /* this object: if Convercy renames a key, this is the only place to    */
  /* change, and `check()` says so in the console.                       */
  /* ------------------------------------------------------------------ */

  var CVC_SWITCHER_KEY = 'cvc_switcher';
  var CVC_CURRENCY_KEY = 'currency';
  var CVC_FORCED_PREFIX = 'cvc_forced:';
  var CVC_MONEY_SELECTOR = '.cvc-money';

  var currency = {
    /**
     * Display currency for a language. Czech is the only CZK storefront;
     * every other language (en, sk, and anything unknown) is EUR.
     */
    forLang: function (lang) {
      if (lang === 'cs') return 'CZK';
      return (config.currencyMap && config.currencyMap[lang]) || 'EUR';
    },

    /**
     * Country Convercy uses to pick a conversion market. Must be a country
     * that actually uses the display currency: CZ for CZK, a eurozone
     * country for EUR. English used to map to US, which made Convercy try
     * USD (or give up and stay on CZK).
     */
    countryForLang: function (lang) {
      if (lang === 'cs') return 'CZ';
      return (config.countryMap && config.countryMap[lang]) || 'DE';
    },

    /**
     * Points Convercy at the currency implied by `lang`.
     *
     * Idempotent on the currency code. Rewriting the app's storage makes it
     * re-apply — which it does by reloading the page — so if the stored
     * currency already matches, leave it completely alone (including whatever
     * country Convercy last wrote). When the currency does change, drop the
     * per-session "already forced" markers so the new code actually applies.
     */
    syncForLang: function (lang) {
      var code = currency.forLang(lang);
      var country = currency.countryForLang(lang);
      if (!code || !country) return null;

      var existing = readJson(local, CVC_SWITCHER_KEY);
      if (
        existing &&
        existing.value &&
        existing.value.currency === code &&
        getItem(local, CVC_CURRENCY_KEY) === code
      ) {
        return code;
      }

      currency.clearForced();
      setItem(
        local,
        CVC_SWITCHER_KEY,
        JSON.stringify({
          value: { currency: code, country: country },
          expire: new Date(Date.now() + config.manualTtlMs).toISOString(),
        })
      );
      setItem(local, CVC_CURRENCY_KEY, code);
      return code;
    },

    /** Drops the per-session "this currency was forced" markers. */
    clearForced: function () {
      if (!session) return;
      try {
        var toRemove = [];
        for (var i = 0; i < session.length; i++) {
          var key = session.key(i);
          if (key && key.indexOf(CVC_FORCED_PREFIX) === 0) toRemove.push(key);
        }
        toRemove.forEach(function (key) {
          removeItem(session, key);
        });
      } catch (e) {
        /* ignore */
      }
    },

    /** Forgets the stored currency entirely (used on a geo mismatch). */
    reset: function () {
      removeItem(local, CVC_SWITCHER_KEY);
      removeItem(local, CVC_CURRENCY_KEY);
      currency.clearForced();
    },

    /**
     * Clears preferences written under an older, longer TTL than the one
     * currently configured, plus a key from a previous implementation.
     */
    purgeStale: function () {
      var obj = readJson(local, CVC_SWITCHER_KEY);
      if (obj && obj.expire) {
        var expiresAt = new Date(obj.expire).getTime();
        if (expiresAt > Date.now() + config.manualTtlMs) {
          removeItem(local, CVC_SWITCHER_KEY);
          removeItem(local, CVC_CURRENCY_KEY);
        }
      }
      removeItem(local, 'pokeccc_lang_auto');
    },

    /** Warns once if the app's markup contract no longer holds. */
    check: function () {
      if (!document.querySelector(CVC_MONEY_SELECTOR)) {
        console.warn(
          LOG_PREFIX,
          'No ' +
            CVC_MONEY_SELECTOR +
            ' markup found — the Convercy money format may have changed, ' +
            'injected prices will not be converted.'
        );
        return false;
      }
      return true;
    },
  };

  /* ------------------------------------------------------------------ */
  /* Routing                                                             */
  /* ------------------------------------------------------------------ */

  /** Strips the active locale prefix, yielding a locale-neutral path. */
  function cleanPath(pathname) {
    var root = config.localeRoot || '/';
    if (root && root !== '/') {
      root = root.replace(/\/$/, '');
      if (pathname === root) return '/';
      if (pathname.indexOf(root + '/') === 0) {
        return pathname.slice(root.length) || '/';
      }
    }
    // Fallback for a prefix Shopify did not report. Anchored with a boundary
    // so handles like /skladem are not mistaken for the /sk locale.
    return pathname.replace(/^\/(cs|sk|en)(?=\/|$)/, '') || '/';
  }

  function joinRoot(root, path) {
    if (!root || root === '/') return path;
    root = root.replace(/\/$/, '');
    return path === '/' ? root : root + path;
  }

  function isCartOrCheckout(pathname) {
    return /^\/(cart|checkouts|checkout)(\/|$)/.test(cleanPath(pathname));
  }

  function isDevHost(hostname) {
    return (
      /^(localhost|127\.0\.0\.1|\[::1\])$/i.test(hostname) ||
      /\.local(host)?$/i.test(hostname)
    );
  }

  function currentDomainKey() {
    return config.isCzDomain ? 'cz' : 'com';
  }

  /**
   * Resolves where `lang` lives, same-domain or across the split.
   *
   * options.path  locale-neutral path to land on (defaults to the current one)
   */
  function resolveUrl(lang, options) {
    if (!isValidLang(lang)) return null;

    var opts = options || {};
    var path = opts.path || cleanPath(window.location.pathname);
    var owner = LANG_OWNER[lang] || 'com';
    var params = new URLSearchParams(window.location.search);
    params.delete(config.crossDomainParam);

    if (owner === currentDomainKey()) {
      var localRoot =
        (config.langRoots && config.langRoots[lang]) ||
        (config.crossRoots && config.crossRoots[lang]) ||
        '/';
      var query = params.toString();
      return joinRoot(localRoot, path) + (query ? '?' + query : '');
    }

    var base = (owner === 'cz' ? config.domainCz : config.domainCom).replace(
      /\/$/,
      ''
    );
    var crossRoot = (config.crossRoots && config.crossRoots[lang]) || '/';
    // Tells the other domain which language was chosen, since localStorage
    // does not cross the origin boundary.
    params.set(config.crossDomainParam, lang);
    return base + joinRoot(crossRoot, path) + '?' + params.toString();
  }

  function canRedirectNow() {
    var count = parseInt(getItem(session, REDIRECT_COUNT_KEY) || '0', 10) || 0;
    if (count >= MAX_REDIRECTS) {
      console.warn(
        LOG_PREFIX,
        'Redirect limit reached this session — staying put to break the loop'
      );
      return false;
    }

    var last = parseInt(getItem(session, REDIRECT_TIME_KEY) || '0', 10) || 0;
    if (last && Date.now() - last < REDIRECT_COOLDOWN_MS) {
      console.warn(
        LOG_PREFIX,
        'An automatic redirect just happened — refusing to chain another'
      );
      return false;
    }

    setItem(session, REDIRECT_COUNT_KEY, String(count + 1));
    setItem(session, REDIRECT_TIME_KEY, String(Date.now()));
    return true;
  }

  /**
   * Called once a page load has settled where it belongs. Safe to clear the
   * cooldown here: a chained second hop is decided before this runs, so the
   * gate has already done its job by then.
   */
  function clearRedirectCount() {
    removeItem(session, REDIRECT_COUNT_KEY);
    removeItem(session, REDIRECT_TIME_KEY);
  }

  /**
   * Navigates to `lang`.
   *
   * options.replace  automatic redirect: no history entry, loop-counted
   * options.path     locale-neutral path override
   * options.force    navigate even when already on `lang`
   */
  function navigate(lang, options) {
    var opts = options || {};

    if (lang === config.locale && !opts.force) {
      return false;
    }

    var target = resolveUrl(lang, opts);
    if (!target) return false;

    var absolute;
    try {
      absolute = new URL(target, window.location.href);
    } catch (e) {
      return false;
    }

    // Hard loop guard: the server is the authority on the current locale, so
    // if it disagrees with us and the URL cannot change, redirecting again
    // would spin forever.
    if (absolute.href === window.location.href) {
      return false;
    }

    if (opts.replace) {
      if (!canRedirectNow()) return false;
      console.log(LOG_PREFIX, 'Redirect ->', absolute.href);
      window.location.replace(absolute.href);
    } else {
      console.log(LOG_PREFIX, 'Navigate ->', absolute.href);
      window.location.href = absolute.href;
    }
    return true;
  }

  var routing = {
    locale: config.locale,
    isCzDomain: !!config.isCzDomain,
    cleanPath: cleanPath,
    isCartOrCheckout: isCartOrCheckout,
    resolveUrl: resolveUrl,
    navigate: navigate,
    clearRedirectCount: clearRedirectCount,
  };

  /* ------------------------------------------------------------------ */
  /* Geo detection — one request per session, shared by all consumers    */
  /* ------------------------------------------------------------------ */

  var geoPromise = null;

  function detectCountry() {
    if (geoPromise) return geoPromise;

    var cached = getItem(session, config.geoCacheKey);
    if (cached) {
      geoPromise = Promise.resolve(cached);
      return geoPromise;
    }

    var root = (config.localeRoot || '/').replace(/\/$/, '');
    var params = new URLSearchParams({
      'country[enabled]': 'true',
      'language[enabled]': 'true',
    });
    var url =
      root + '/browsing_context_suggestions.json?' + params.toString();

    geoPromise = fetch(url)
      .then(function (res) {
        if (!res.ok) throw new Error('HTTP ' + res.status);
        return res.json();
      })
      .then(function (data) {
        var detected =
          data.detected_values &&
          data.detected_values.country &&
          data.detected_values.country.handle;

        if (!detected) {
          var suggestion =
            data.suggestions &&
            data.suggestions[0] &&
            data.suggestions[0].parts &&
            data.suggestions[0].parts.country;
          detected = suggestion && suggestion.handle;
        }
        if (!detected) throw new Error('No country in suggestions');

        setItem(session, config.geoCacheKey, detected);
        return detected;
      })
      .catch(function (err) {
        // Deliberately NOT cached: a transient failure should not disable
        // detection for the rest of the session. Resolving to null means
        // "unknown" — callers must leave the server-rendered state alone.
        console.warn(LOG_PREFIX, 'Country detection unavailable', err);
        geoPromise = null;
        return null;
      });

    return geoPromise;
  }

  function countryToLang(countryCode) {
    if (countryCode === 'CZ') return 'cs';
    if (countryCode === 'SK') return 'sk';
    return 'en';
  }

  /* ------------------------------------------------------------------ */
  /* Pre-paint pass                                                      */
  /* ------------------------------------------------------------------ */

  /**
   * The visitor's primary browser language, when it is one we serve.
   *
   * OFF by default, because it overrides the detected country: a Czech browser
   * in Hungary would stay on the Czech store and never reach geo detection,
   * which is not what the shipping/currency split wants. Enable it only to
   * trade that correctness for skipping the geo round trip.
   */
  function browserLang() {
    if (!config.useBrowserLanguage) return null;

    var list =
      navigator.languages && navigator.languages.length
        ? navigator.languages
        : navigator.language
        ? [navigator.language]
        : [];
    if (!list.length) return null;

    var primary = String(list[0]).toLowerCase().split('-')[0];
    // Only cs/sk are decided here. "en" is left to geo, which still has to
    // separate an English speaker in Czechia from one abroad.
    return primary === 'cs' || primary === 'sk' ? primary : null;
  }

  function shouldAbort() {
    // Theme editor and any other embedding: never touch navigation.
    if (window.self !== window.top) return true;
    if (window.location.hostname.indexOf('shopify.com') !== -1) return true;
    return false;
  }

  function runFastPath() {
    var result = {
      lang: null,
      source: null,
      redirecting: false,
      redirectsAllowed: false,
    };

    var path = window.location.pathname;
    var hostname = window.location.hostname;

    result.redirectsAllowed =
      !config.redirectDisabled &&
      !isDevHost(hostname) &&
      // Carts do not survive a hop between pokeccc.cz and pokeccc.com — the
      // cart cookie is per-domain. Never move a shopper off these pages.
      !isCartOrCheckout(path);

    var urlLang = new URLSearchParams(window.location.search).get(
      config.crossDomainParam
    );

    if (isValidLang(urlLang)) {
      result.lang = urlLang;
      result.source = 'url';
      prefs.setLang(urlLang);
    } else {
      var stored = prefs.getLang();
      if (stored) {
        result.lang = stored;
        result.source = 'manual';
      } else {
        var browser = browserLang();
        if (browser) {
          result.lang = browser;
          result.source = 'browser';
          // Not persisted: it is a hint, not a choice.
        }
      }
    }

    if (result.redirectsAllowed && result.lang && result.lang !== config.locale) {
      // About to leave this page. Point Convercy at the destination language
      // so a same-domain hop (e.g. / → /sk) does not keep the previous currency.
      currency.syncForLang(result.lang);
      result.redirecting = navigate(result.lang, { replace: true });
    } else {
      // This is the page the visitor will actually see. Currency follows the
      // rendered language: CZK on Czech, EUR on every other language.
      currency.syncForLang(config.locale);
    }

    if (!result.redirecting) clearRedirectCount();

    return result;
  }

  var abort = shouldAbort();

  currency.purgeStale();

  var fastPath = abort
    ? { lang: null, source: 'aborted', redirecting: false, redirectsAllowed: false }
    : runFastPath();

  window.PokeCCC = {
    config: config,
    ready: true,
    abort: abort,
    isValidLang: isValidLang,
    prefs: prefs,
    currency: currency,
    routing: routing,
    geo: { detect: detectCountry, countryToLang: countryToLang },
    fastPath: fastPath,
  };
})();
